import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  SingleArrowLeftIcon,
  SingleArrowRightIcon,
} from "../../assets";

import { AppDispatch } from "../../redux/store";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../../utils/constants";
import "../../assets/scss/abstracts/_pagination.scss";
import PaginationModal from "../modals/PaginationModal";
import useFormChangeHandler from "../../hooks/useFormChangeHandler";

interface ChildPaginationProps {
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
  totalItems: number;
  setCurrentPage: (page: number) => void; // We use this to update page
  limit?: number;
  onLimitChange?: (limit: number) => void;
  /**
   * Ask before paging while a form has unsaved changes (the default). Turn it
   * off for a list that pages on its own, apart from the form on screen.
   */
  confirmUnsavedChanges?: boolean;
}

const ChildPagination: React.FC<ChildPaginationProps> = ({
  currentPage,
  totalPages,
  previousPage,
  nextPage,
  totalItems,
  setCurrentPage,
  limit = DEFAULT_PAGE_SIZE,
  onLimitChange,
  confirmUnsavedChanges = true,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [inputPage, setInputPage] = useState<number | "">(currentPage);
  const [pendingPage, setPendingPage] = useState<number | null>(null);
  const [isChanges, setIsChanges] = useState<boolean>(false);
  const { isFormChanged, handleUpdateNavigation } = useFormChangeHandler();
  /**
   * Handles page change events.
   *
   * @param page - The page number to navigate to.
   */
  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      if (confirmUnsavedChanges && isFormChanged) {
        setPendingPage(page);
        setIsChanges(true);
      } else {
        setCurrentPage(page);
      }
      //setCurrentPage(page); // Use the passed-in setCurrentPage to update the page
    }
  };

    /**
   * Confirms the page change after saving or discarding unsaved changes.
   */
    const confirmPageChange = () => {
      if (pendingPage !== null) {
        handleUpdateNavigation(); // Trigger form change handling
        setCurrentPage(pendingPage);
        //dispatch(setPage(pendingPage)); // Dispatch the stored pending page
        setIsChanges(false); // Close the modal
        setPendingPage(null); // Reset pending page
      }
    };

  /**
   * Calculates the page numbers to display in the pagination component.
   *
   * @returns An array of page numbers to display.
   */
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const halfMaxPagesToShow = Math.floor(maxPagesToShow / 2);

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= halfMaxPagesToShow) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pageNumbers.push(i);
        }
      } else if (currentPage + halfMaxPagesToShow >= totalPages) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        for (
          let i = currentPage - halfMaxPagesToShow;
          i <= currentPage + halfMaxPagesToShow;
          i++
        ) {
          pageNumbers.push(i);
        }
      }
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  const itemsPerPage = limit;
  const startIndex =
    totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  useEffect(() => {
    setInputPage(currentPage); // Sync the input field with the current page
  }, [currentPage]);

  return (
    <>
      <div className="pagination" style={{ position: "relative" }}>
        <div className="pagination__result">
          {startIndex} - {endIndex} of {totalItems} results
        </div>
        <nav
          aria-label="pagination navigation"
          className="MuiPagination-root MuiPagination-outlined css-1oj2twp-MuiPagination-root"
        >
          <ul className="MuiPagination-ul css-wjh20t-MuiPagination-ul">
            <li>
              <button
                className="MuiButtonBase-root Mui-disabled MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded Mui-disabled MuiPaginationItem-firstLast css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                type="button"
                aria-label="Go to first page"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(1)}
              >
                <DoubleArrowLeftIcon />
              </button>
            </li>
            <li>
              <button
                className="MuiButtonBase-root Mui-disabled MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded Mui-disabled MuiPaginationItem-previousNext css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                type="button"
                aria-label="Go to previous page"
                disabled={!previousPage}
                onClick={() => handlePageChange(previousPage ?? 1)}
              >
                <SingleArrowLeftIcon />
              </button>
            </li>
            {pageNumbers?.map((page, index) => (
              <li key={index}>
                <button
                  className={`MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded MuiPaginationItem-page css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root ${
                    currentPage === page ? "Mui-selected" : ""
                  }`}
                  type="button"
                  aria-current={currentPage === page ? "true" : undefined}
                  aria-label={`Go to page ${page}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              </li>
            ))}
            {totalPages > 5 && currentPage + 2 < totalPages && (
              <li>
                <span className="pagination-ellipsis">...</span>
              </li>
            )}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <li>
                <button
                  className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded MuiPaginationItem-page css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                  type="button"
                  aria-label={`Go to page ${totalPages}`}
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>
              </li>
            )}
            <li>
              <button
                className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded MuiPaginationItem-previousNext css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                type="button"
                aria-label="Go to next page"
                disabled={!nextPage}
                onClick={() => handlePageChange(nextPage ?? totalPages)}
              >
                <SingleArrowRightIcon />
              </button>
            </li>
            <li>
              <button
                className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded MuiPaginationItem-firstLast css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                type="button"
                aria-label="Go to last page"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(totalPages)}
              >
                <DoubleArrowRightIcon />
              </button>
            </li>
          </ul>
        </nav>
        <div className="pagination__actions">
          {onLimitChange && (
            <div className="pagination__actions__item">
              <h6>Items Per Page</h6>
              <select
                value={limit}
                onChange={(e) => {
                  onLimitChange(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="formField__select"
                style={{ width: "65px" }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          )}
          <div className="pagination__actions__item">
            <h6>Go to page</h6>
            <input
              type="number"
              className="formField__input sm"
              value={inputPage}
              onChange={(e) => {
                const page = parseInt(e.target.value, 10);
                if (!isNaN(page) && page > 0 && page <= totalPages) {
                  setInputPage(page);
                } else {
                  setInputPage("");
                }
              }}
            />
            <button
              className="btn btn__primary"
              onClick={() => handlePageChange(inputPage as number)}
            >
              Go
            </button>
          </div>
        </div>
      </div>
      {isChanges && (
        <PaginationModal
          onCancel={() => {
            setIsChanges(false);
          }}
          onConfirm={confirmPageChange}
        />
      )}
    </>
  );
};

export default ChildPagination;
